import * as Paho from 'paho-mqtt';
import { call } from 'redux-saga/effects';
import { fetchSignedWebSocketUrl } from 'common/store/session/actions';
import store from '../store';
import { IndexPushChannel } from '../types';

const uuid = require('uuid');

interface TopicCallbackMap {
    [key: string]: any;
}

const topicCallbackMap: TopicCallbackMap = {};
const RECONNECT_INTERVAL = 20 * 1000;
let awtClient: Paho.Client;

const awsIOTService = {
    reconnectClient(): void {
        setTimeout(
            () => {
                window.console.info('Reconnecting to AWS IoT');
                store.dispatch(fetchSignedWebSocketUrl());
            },
            RECONNECT_INTERVAL
        );
    },
    onConnectionLost(e: Paho.MQTTError): void {
        window.console.info('Client disconnected from AWS IoT', e);
        this.reconnectClient();
    },
    onMessageArrived(message: Paho.Message): void {
        const messageObject = JSON.parse(message.payloadString);
        const callback = topicCallbackMap[message.destinationName];
        if (typeof callback === 'function') {
            callback(messageObject);
        }
        window.console.info('Message from AWS IoT', message.destinationName, message.payloadString);
    },
    subscribeToTopic(topic: string, callback: any): void {
        window.console.log(`Client subscribing to ${topic}`);

        if (awtClient) {
            awtClient.subscribe(topic);
        }

        topicCallbackMap[topic] = callback;
    },
    unsubscribeFromTopic(topic: string): void {
        window.console.log(`Client unsubscribing from ${topic}`);

        if (awtClient) {
            awtClient.unsubscribe(topic);
        }

        delete topicCallbackMap[topic];
    },
    buildTopic(topic: string, postfix?: any): string {
        let completeTopic = topic;
        const devEnv = (window as any).deployEnvironment;
        if (postfix) {
            completeTopic = `${completeTopic}_${postfix}`;
        }

        return `${devEnv}_${completeTopic}`;
    },
    onIOTConnectSuccess(client: Paho.Client): void {
        awtClient = client;
        window.console.log('Client connected to AWS IoT, listen to topics');
    },
    onIOTConnectFailure(e: Paho.MQTTError): void {
        window.console.log('Client failed to connect to AWS IoT', e);
        this.reconnectClient();
    },
    setupClient(signedWebSocketUrl: string): void {
        if (window.Paho === undefined) {
            window.Paho = { MQTT: Paho };
        }

        if (!signedWebSocketUrl) {
            window.console.warn('No web socket url available!');
            return;
        }
        const client: Paho.Client = new Paho.Client(signedWebSocketUrl, uuid.v4());
        const connectOptions: Paho.ConnectionOptions = {
            useSSL: true,
            timeout: 3,
            mqttVersion: 4,
            onSuccess: () => this.onIOTConnectSuccess(client),
            onFailure: (e: Paho.MQTTError) => this.onIOTConnectFailure(e),
        };

        client.onMessageArrived = (message: Paho.Message) => {
            this.onMessageArrived(message);
        };

        client.onConnectionLost = (e: Paho.MQTTError) => {
            this.onConnectionLost(e);
        };

        client.connect(connectOptions);
    },
    subscribeActionsToIndexPushChannel: function* subscribeToIndexPushChannel(
        featureId: string,
        response: any,
        actions: Array<any> = [],
        channelName: IndexPushChannel
    ) {
        const { message, error, success } = response;

        if (success) {
            const pushChannel = response[channelName];
            if (pushChannel) {
                const topic = awsIOTService.buildTopic(pushChannel);
                const callback = (message: Paho.Message) => {
                    if (message) {
                        actions.forEach((action) => {
                            if (action.actionName && action.actionArgs) {
                                const { actionName, actionArgs } = action;
                                store.dispatch(actionName(...actionArgs));
                            } else if (typeof action === 'object') {
                                store.dispatch(action);
                            } else {
                                store.dispatch(action(featureId));
                            }
                        });
                    }
                };
                yield call(awsIOTService.subscribeToTopic, topic, callback.bind(awsIOTService));
            }
        } else if (error) {
            console.error(`${channelName} subscribe failed`, error);
        } else if (message) {
            console.error(`${channelName} subscribe failed`, message);
        }
    },
    createActionObject(actionName: any, actionArgs: Array<any>): any {
        return { actionName, actionArgs };
    },
};


export default awsIOTService;
